import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { useAuth } from '../context/AuthContext'
import { useModal } from '../context/ModalContext'
import { useUsers } from '../hooks/useUsers'
import { usePageTitle } from '../components/DocumentTitle'
import NetworkList from '../components/network/NetworkList'
import NetworkFilterPanel from '../components/network/NetworkFilterPanel'
import { NetworkWindowBody } from '../components/network/NetworkWindowBody'
import { personName } from '../components/network/profileFormat'
import '../components/network/Network.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconMe = () => (
  <svg {...svgProps}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const SCOPES = [
  { id: 'all', label: 'Everyone' },
  { id: 'connections', label: 'Connections' },
  { id: 'requests', label: 'Requests' },
]

const EMPTY_FILTERS = { occupation: [], company: [], school: [] }

function emptyCopy(scope, searching) {
  if (searching) return 'No one matches.'
  if (scope === 'connections') return 'No connections yet.'
  if (scope === 'requests') return 'No requests.'
  return 'No people to show.'
}

// People directory: search and filters in the sidebar, one profile per window.
function NetworkPage() {
  const { user } = useAuth()
  const { openModal } = useModal()
  const [q, setQ] = useState('')
  const [scope, setScope] = useState('all')
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [filterOpen, toggleFilter] = useFilterOpen('ct-network-filter-open')
  const [notice, setNotice] = useState('')
  const [revision, setRevision] = useState(0)
  const [names, setNames] = useState({})

  const grid = useWindowGrid({
    storageKey: 'ct-grid-network-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'network',
  })

  const { users, facets, truncated, requestCount, status, reload } = useUsers({
    q: q.trim(),
    scope,
    occupation: filters.occupation,
    company: filters.company,
    school: filters.school,
  })

  const remember = useCallback((person) => {
    if (!person?._id) return
    const name = personName(person)
    setNames((prev) => (prev[person._id] === name ? prev : { ...prev, [person._id]: name }))
  }, [])

  useEffect(() => {
    users.forEach(remember)
  }, [users, remember])

  const activeId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((entry) => entry.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  const openUser = useCallback(
    (person) => {
      if (!person?._id) return
      remember(person)
      grid.selectEntity({ type: 'network', entityId: person._id })
    },
    [grid, remember]
  )

  const refresh = useCallback(() => {
    setRevision((n) => n + 1)
    reload()
  }, [reload])

  const editProfile = useCallback(() => {
    openModal('profile-edit', { profile: user, onSaved: refresh })
  }, [openModal, user, refresh])

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(`You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`)
      return
    }
    grid.addWindow({ type: 'network' })
  }, [grid])

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      return names[win.entityId] || 'Loading…'
    },
    [names]
  )
  usePageTitle(activeId ? names[activeId] || '' : '')

  const renderWindow = useCallback(
    (win) => (
      <NetworkWindowBody
        userId={win.entityId}
        revision={revision}
        onOpenUser={openUser}
        onChanged={refresh}
        onProfile={remember}
      />
    ),
    [revision, openUser, refresh, remember]
  )

  const renderMenuItems = useCallback(
    (win) => {
      if (!win.entityId || String(win.entityId) !== String(user?._id)) return null
      return (
        <button type="button" role="menuitem" onClick={editProfile}>
          <IconMe />
          Edit profile
        </button>
      )
    },
    [user?._id, editProfile]
  )

  const filtered = Boolean(filters.occupation.length || filters.company.length || filters.school.length)
  const searching = Boolean(q.trim() || filtered)

  return (
    <MainLayout
      storageId="network-layout"
      navLocation={<NavLocation title="Network" />}
      navActions={[
        { key: 'add-window', label: 'Add window', icon: <IconAddWindow />, onClick: handleAddWindow },
        { key: 'me', label: 'My profile', icon: <IconMe />, onClick: () => user?._id && openUser(user) },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <NavFilterLead
            open={filterOpen}
            active={filtered}
            onToggle={toggleFilter}
            onClear={() => setFilters(EMPTY_FILTERS)}
            clearDisabled={!filtered}
          />
          <div className={'net-tools' + (filterOpen ? ' is-with-panel' : ' is-last')}>
            <input
              className="ct-input"
              type="text"
              value={q}
              placeholder="Search people…"
              onChange={(event) => setQ(event.target.value)}
            />
            <div className="ct-toggle" role="group" aria-label="Who to show">
              {SCOPES.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={'ct-toggle-btn' + (scope === option.id ? ' is-active' : '')}
                  onClick={() => setScope(option.id)}
                >
                  {option.id === 'requests' && requestCount ? `${option.label} (${requestCount})` : option.label}
                </button>
              ))}
            </div>
          </div>
          {filterOpen ? (
            <NetworkFilterPanel
              facets={facets}
              values={filters}
              onChange={(key, next) => setFilters((prev) => ({ ...prev, [key]: next }))}
            />
          ) : null}
          <NetworkList
            users={users}
            status={status}
            truncated={truncated}
            selectedId={activeId}
            onSelect={openUser}
            empty={emptyCopy(scope, searching)}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice ? <div className="sr-grid-notice">{notice}</div> : null}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select a person from the list."
            />
          </div>
        </div>
      }
    />
  )
}

export default NetworkPage
